import {createAction, createAsyncThunk} from '@reduxjs/toolkit'

import {eq, minZero} from '@shared/helpers/lang'

import {
    ChargePointPolicy,
    ElementId,
    MaximumUsingChargePointAmount,
    PayMethod,
    PayMethodType,
    PointPaymentCase,
} from '@web/core/constants'
import {isPointChargingAvailable, getRechargePointAmount} from '@web/helpers/policies'
import {rechargePaymentInfoSelector} from '@web/selectors/paymentMethodInfo'
import {naverPayPointInfoSelector, payLaterInfoSelector} from '@web/selectors/pointPaymentInfo'
import {AccountState, rechargeAccountInfoSelector} from '@web/slices/account/slice'
import {CardState} from '@web/slices/card/slice'
import {chargePointSelector, ChargePointState, payMoneyInfoSelector} from '@web/slices/chargePoint/slice'
import {CommonState} from '@web/slices/common/slice'
import {orderAmountInfoSelector, OrderTotalState} from '@web/slices/orderTotal/slice'
import {payLaterSelector, PayLaterState} from '@web/slices/payLater/slice'
import {PaymentMethodState, paymentSelector, payMethodAvailableSelector} from '@web/slices/paymentMethod/slice'
import {naverPayPointSelector, PointPaymentState} from '@web/slices/pointPayment/slice'
import {RewardPointState} from '@web/slices/rewardPoint/slice'

type ActionPayload = {
    usingNaverPointAmount: number
    usingPayLaterPointAmount: number
    enabledPayLater: boolean
    usePayLaterAll: boolean
    unusablePayMethods: PayMethodType[]
    paymentAmount: number
    payMethods: PayMethodType[]
    rechargingPointAmount: number
}

type RootState = {
    common: CommonState
    orderTotal: OrderTotalState
    pointPayment: PointPaymentState
    chargePoint: ChargePointState
    payLater: PayLaterState
    rewardPoint: RewardPointState
    paymentMethod: PaymentMethodState
    account: AccountState
    card: CardState
}

type ThunkApi = {
    state: RootState
    rejectValue: string
}

function getActionPayload(
    state: RootState,
    {
        usingNaverPoint,
        naverPointAmount,
        usingPayLater,
        reloadablePayLater,
        enabledPayLater,
    }: {
        usingNaverPoint: number
        naverPointAmount: number
        usingPayLater: number
        reloadablePayLater: number
        enabledPayLater: boolean
    },
): ActionPayload {
    const {orderAmount} = orderAmountInfoSelector(state)
    const {isPayMoneyUser, payMoneyUserStatus, isUsedLikePayMoney, chargePointPolicy} = payMoneyInfoSelector(state)
    const {
        amount: chargePointAmount,
        reloadable: {amount: reloadableRechargePointAmount},
    } = chargePointSelector(state)
    const {
        selected: {payAvailable: canUseRechargeAccount},
    } = rechargeAccountInfoSelector(state)
    const {
        selected: {payMethods, prevPayMethod},
    } = paymentSelector(state)
    const {isRechargePointAvailable} = payMethodAvailableSelector(state)
    const {possibleMaxUsingAmount: maxUsingAmount} = naverPayPointInfoSelector(state)
    const {canUsePayment: canUsePaymentPayLater, possibleMaxPayLaterAmount} = payLaterInfoSelector(state)
    const {complex} = rechargePaymentInfoSelector(state)
    const {
        complex: {selectionType},
    } = paymentSelector(state)

    const possibleMaxUsingAmount = Math.max(maxUsingAmount, 0)

    const enablePayLater = canUsePaymentPayLater && possibleMaxUsingAmount >= naverPointAmount ? enabledPayLater : false

    // 후불결제 사용시 네이버 포인트 최대화
    const usingNaverPointAmount = enablePayLater
        ? possibleMaxUsingAmount
        : Math.max(Math.min(usingNaverPoint, possibleMaxUsingAmount), 0)

    const usingPayLaterAmount = enablePayLater
        ? Math.max(Math.min(usingPayLater, Math.min(reloadablePayLater, possibleMaxPayLaterAmount)), 0)
        : 0
    const usingPointPaymentAmount = usingNaverPointAmount + usingPayLaterAmount
    const paymentAmount = orderAmount - usingPointPaymentAmount
    const usePayLaterAll = usingPayLaterAmount > 0 && usingPayLaterAmount === possibleMaxPayLaterAmount

    const unusablePayMethods = (function (): PayMethodType[] {
        if (enablePayLater && paymentAmount > 0) {
            return [...(isPayMoneyUser ? [] : [PayMethod.RECHARGE_POINT]), PayMethod.SKIP, PayMethod.VIRTUAL_ACCOUNT]
        }
        return []
    })()

    const canUseChargePointAccount =
        isPointChargingAvailable({payMoneyUserStatus}) || eq(chargePointPolicy, ChargePointPolicy.PAYMONEY)
    const usingChargePointAmount = canUseChargePointAccount
        ? minZero(Math.min(paymentAmount, Math.min(chargePointAmount, MaximumUsingChargePointAmount)))
        : 0

    const selectedPayMethods = [
        ...payMethods.filter((payMethod) => payMethod === PayMethod.RECHARGE_POINT),
        prevPayMethod,
    ].filter((payMethod) => !unusablePayMethods.includes(payMethod))

    const rechargingPointAmount = (function () {
        // 포인트 사용으로 남은 금액이 없다면 충전포인트 초기화
        if (!selectedPayMethods.includes(PayMethod.RECHARGE_POINT) || paymentAmount <= 0) {
            return 0
        }
        return getRechargePointAmount({
            orderAmount: paymentAmount,
            usingChargePointAmount,
            rechargePoint: {
                enabled: true,
                usePointAll: true,
                rechargingAmount: 0,
                reloadableRechargePointAmount,
                canUseRechargeAccount,
                canUseChargePointAccount,
            },
            payMoneyStateAvailable: isUsedLikePayMoney,
            chargePointAmount,
            payLaterUsed: enablePayLater,
            availableMethod: isRechargePointAvailable,
        })
    })()

    const usingPayMethodAmount = minZero(
        paymentAmount - Math.min(rechargingPointAmount + usingChargePointAmount, MaximumUsingChargePointAmount),
    )

    const onlySinglePaymentMethodSelectable = selectionType === 'SEQUENTIAL'
    const isRechargeComplexSelected =
        selectedPayMethods.length > 1 && selectedPayMethods.includes(PayMethod.RECHARGE_POINT)
    const isComplexAvailable = complex.available && usingPayMethodAmount > 0

    return {
        usingNaverPointAmount,
        usingPayLaterPointAmount: usingPayLaterAmount,
        enabledPayLater: enablePayLater,
        usePayLaterAll,
        unusablePayMethods,
        payMethods: (function () {
            if (
                (isRechargeComplexSelected && onlySinglePaymentMethodSelectable) ||
                // 충전결제가 선택되었는데, 복합결제가 안되면 충전결제만 세팅
                (isRechargeComplexSelected && !isComplexAvailable)
            ) {
                return [PayMethod.RECHARGE_POINT]
            }
            return selectedPayMethods
        })(),
        rechargingPointAmount,
        paymentAmount,
    }
}

const createActionForChangeNaverPointUsingAmount = (actionName: string) =>
    createAsyncThunk<ActionPayload, number, ThunkApi>(actionName, (usingAmount, {getState, rejectWithValue}) => {
        const state = getState()
        const {amount} = naverPayPointSelector(state)
        if (usingAmount > amount) {
            return rejectWithValue(PointPaymentCase.NAVER_POINT_OVER_USING_POINT)
        }
        return getActionPayload(state, {
            usingNaverPoint: usingAmount,
            naverPointAmount: amount,
            usingPayLater: 0,
            reloadablePayLater: 0,
            enabledPayLater: false,
        })
    })

/** 네이버 포인트 사용량 변경 */
export const changeNaverPoint = createActionForChangeNaverPointUsingAmount('pointPayment/naverPoint/change')

/** 네이버 포인트 초기화 */
export const resetNaverPoint = createAsyncThunk<ActionPayload, void, ThunkApi>(
    'pointPayment/naverPoint/reset',
    (_, {getState}) => {
        const state = getState()
        return getActionPayload(state, {
            usingNaverPoint: 0,
            naverPointAmount: 0,
            usingPayLater: 0,
            reloadablePayLater: 0,
            enabledPayLater: false,
        })
    },
)

/** 네이버페이 포인트 전액사용 토글 */
export const toggleUsePointAll = createAction<boolean>('pointPayment/naverPoint/toggleUsePointAll')

/** 네이버페이 포인트 전액사용 */
export const usingAllNaverPoint = createAsyncThunk<ActionPayload, void, ThunkApi>(
    'pointPayment/naverPoint/usingAll',
    (_, {getState}) => {
        const state = getState()
        const {amount} = naverPayPointSelector(state)
        const {enabled, usingAmount: usingPayLater, availableAmount} = payLaterSelector(state)
        return getActionPayload(state, {
            usingNaverPoint: amount,
            naverPointAmount: amount,
            usingPayLater,
            reloadablePayLater: availableAmount,
            enabledPayLater: enabled,
        })
    },
)

/** 네이버페이 포인트 결제 > 항상 보유 포인트 전액 사용 */
export const changeAlwaysUsesAllNaverPoint = createAsyncThunk<
    ActionPayload & {
        usePointAll: boolean
    },
    boolean,
    ThunkApi
>('pointPayment/naverPoint/changeUses', (usePointAll, {getState}) => {
    const state = getState()
    const {amount, usingAmount} = naverPayPointSelector(state)
    const {enabled, usingAmount: usingPayLater, availableAmount} = payLaterSelector(state)
    const usingNaverPoint = usePointAll ? amount : usingAmount
    return {
        usePointAll,
        ...getActionPayload(state, {
            usingNaverPoint,
            naverPointAmount: amount,
            usingPayLater,
            reloadablePayLater: availableAmount,
            enabledPayLater: enabled,
        }),
    }
})

export const changePointAndToggle = createAsyncThunk<
    ActionPayload & {
        usePointAll: boolean
    },
    {usingAmount: number; usePointAll: boolean},
    ThunkApi
>('pointPayment/point/changePointAndToggle', async ({usingAmount, usePointAll}, {getState, rejectWithValue}) => {
    const state = getState()
    const {amount} = naverPayPointSelector(state)
    if (usingAmount > amount) {
        return rejectWithValue(PointPaymentCase.NAVER_POINT_OVER_USING_POINT)
    }
    return {
        usePointAll,
        ...getActionPayload(state, {
            usingNaverPoint: usingAmount,
            naverPointAmount: amount,
            usingPayLater: 0,
            reloadablePayLater: 0,
            enabledPayLater: false,
        }),
    }
})

/** 후불결제 포인트 변경 */
export const changePayLaterPoint = createAsyncThunk<ActionPayload, number, ThunkApi>(
    'pointPayment/payLater/change',
    (usingPoint, {getState, rejectWithValue}) => {
        const state = getState()
        const {usingAmount, amount} = naverPayPointSelector(state)
        const {enabled, availableAmount} = payLaterSelector(state)
        // 입력한 후불결제 금액이 최대 이용가능 금액보다 많다면,
        if (usingPoint > availableAmount) {
            return rejectWithValue('PAY_LATER_OVER_USING_POINT')
        }
        return getActionPayload(state, {
            usingNaverPoint: usingAmount,
            naverPointAmount: amount,
            usingPayLater: usingPoint,
            reloadablePayLater: availableAmount,
            enabledPayLater: enabled,
        })
    },
)

/**
 * 후불결제 포인트 초기화
 **/
export const resetPayLaterPoint = createAsyncThunk<ActionPayload, void, ThunkApi>(
    'pointPayment/payLater/reset',
    (usePointAll, {getState}) => {
        const state = getState()
        const {usingAmount, amount} = naverPayPointSelector(state)
        const {enabled, availableAmount} = payLaterSelector(state)
        return getActionPayload(state, {
            usingNaverPoint: usingAmount,
            naverPointAmount: amount,
            usingPayLater: 0,
            reloadablePayLater: availableAmount,
            enabledPayLater: enabled,
        })
    },
)

/**
 * 사용가능한 전체 금액을 사용합니다.
 */
export const usingAllPayLaterPoint = createAsyncThunk<ActionPayload, void, ThunkApi>(
    'pointPayment/payLater/usingAll',
    (_, {getState}) => {
        const state = getState()
        const {usingAmount, amount} = naverPayPointSelector(state)
        const {enabled, availableAmount} = payLaterSelector(state)
        return getActionPayload(state, {
            usingNaverPoint: usingAmount,
            naverPointAmount: amount,
            usingPayLater: availableAmount,
            reloadablePayLater: availableAmount,
            enabledPayLater: enabled,
        })
    },
)

/**
 * 후불결제 사용 action
 **/
export const enablePayLaterPoint = createAsyncThunk<ActionPayload, boolean, ThunkApi>(
    'pointPayment/payLater/enable',
    (checked, {getState, rejectWithValue}) => {
        const state = getState()
        const {amount, usingAmount} = naverPayPointSelector(state)
        const {availableAmount} = payLaterSelector(state)
        // 페이 포인트 소진 여부를 확인하고, 사용자의 결정에 따라 소진 또는 액션을 종료합니다.
        if (checked && amount > usingAmount) {
            return rejectWithValue(PointPaymentCase.PAY_LATER_EXHAUST_ALL_POINT_USE)
        }
        const usingPayLaterPointAmount = checked ? availableAmount : 0
        return getActionPayload(state, {
            usingNaverPoint: usingAmount,
            naverPointAmount: amount,
            usingPayLater: usingPayLaterPointAmount,
            reloadablePayLater: availableAmount,
            enabledPayLater: checked,
        })
    },
)

/** 포인트 전액 사용 및 후불결제 enable */
export const enablePayLaterPointByAllPoint = createAsyncThunk<ActionPayload, void, ThunkApi>(
    'pointPayment/payLater/enableAllPoint',
    (_, {getState}) => {
        const state = getState()
        const {amount} = naverPayPointSelector(state)
        const {availableAmount} = payLaterSelector(state)
        return getActionPayload(state, {
            usingNaverPoint: amount,
            naverPointAmount: amount,
            usingPayLater: availableAmount,
            reloadablePayLater: availableAmount,
            enabledPayLater: true,
        })
    },
)

/** 권역 배송비 변경 에 따른 포인트 초기화 */
export const changeDeliveryAreaExtraFee = createAsyncThunk<ActionPayload, void, ThunkApi>(
    'pointPayment/changeDeliveryAreaExtraFee',
    (_, {getState}) => {
        const state = getState()
        const {amount, usePointAll} = naverPayPointSelector(state)
        const {availableAmount} = payLaterSelector(state)
        return getActionPayload(state, {
            usingNaverPoint: usePointAll ? amount : 0,
            naverPointAmount: amount,
            usingPayLater: 0,
            reloadablePayLater: availableAmount,
            enabledPayLater: false,
        })
    },
)

/** 변경된 금엑에 맞춰 포인트 사용 변경 */
export const updatePointPayment = createAsyncThunk<ActionPayload, void, ThunkApi>(
    'pointPayment/update',
    (_, {getState}) => {
        const state = getState()
        const {amount, usingAmount: usingNaverPoint} = naverPayPointSelector(state)
        const {availableAmount, usingAmount: usingPayLater, enabled} = payLaterSelector(state)
        return getActionPayload(state, {
            usingNaverPoint,
            naverPointAmount: amount,
            usingPayLater,
            reloadablePayLater: availableAmount,
            enabledPayLater: enabled,
        })
    },
)

/** 후불결제 사용 요청 action */
export const requestUsingPayLater = createAction('pointPayment/requestUsingPayLater', () => {
    document.getElementById(ElementId.POINT_SECTION)?.scrollIntoView()
    document.getElementById(ElementId.PAY_LATER_USED)?.click()
    return {
        payload: ElementId.PAY_LATER_USED,
    }
})
